import classNames from 'classnames';
import { get, map, includes } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import 'styles/tailwind.css';

import CardCarousel from 'components/CardCarousel';
import EmailSignupModule from 'components/EmailSignupModule';
import { Grid, Cell } from 'components/Grid';
import HeroCarousel from 'components/HeroCarousel';
import HeroSlide from 'components/HeroSlide';
import SecondaryPromoHero from 'components/HomepageV2SecondaryPromoHero';
import PageSectionAssetDecoratedCarousel from 'components/PageSectionAssetDecoratedCarousel';
import PageSectionCTAImages from 'components/PageSectionCTAImages';
import PageSectionFeaturedSlideShow from 'components/PageSectionFeaturedSlideShow';
import PageSectionImage from 'components/PageSectionImage';
import PageSectionImageCopy from 'components/PageSectionImageCopy';
import PageSectionSecondaryNavigation from 'components/PageSectionSecondaryNavigation';
import PageSectionShowcase from 'components/PageSectionShowcase';
import PageSectionTestimonialCarousel from 'components/PageSectionTestimonialCarousel';
import VideoModal from 'components/VideoModal';
import { PAGE_SECTIONS, HERO_TYPES } from 'constants/landingPage';
import { mediumUp, largeUp } from 'constants/mediaQueries';
import CardCarouselModel from 'models/CardCarousel';
import HeroModel from 'models/Hero';
import PageSectionAssetDecoratedCarouselModel from 'models/PageSectionAssetDecoratedCarousel';
import PageSectionFeaturedSlideShowModel from 'models/PageSectionFeaturedSlideShow';
import PageSectionTestimonialCarouselModel from 'models/PageSectionTestimonialCarousel';
import SecondaryPromoHeroModel from 'models/SecondaryPromoHero';
import Theme from 'styles/theme';

const PERFORM_LANDING_PAGE = 'sustainable-activewear';

class LandingPage extends React.PureComponent {
  constructor() {
    super();

    this.state = { modalVideoUrl: '' };
  }

  openVideoModal = url => {
    this.setState({ modalVideoUrl: url });
    this.props.openModal();
  };

  onEmailSubmit = async (email, acceptsMarketing) => {
    const { signUpForNewsletter } = this.props;

    signUpForNewsletter({
      emailAddress: email,
      origination: 'signupModule',
      acceptsMarketing,
    });
  };

  isPerformLanding = () => {
    return includes(window.location.href, PERFORM_LANDING_PAGE);
  };

  renderPageSection = data => {
    const { trackEvent } = this.props;

    const type = get(data, '_contentType');

    switch (type) {
      case PAGE_SECTIONS.CTA_IMAGES:
        return <PageSectionCTAImages {...data} trackEvent={trackEvent} />;
      case PAGE_SECTIONS.IMAGE:
        return <PageSectionImage image={data} />;
      case PAGE_SECTIONS.SHOWCASE:
        return <PageSectionShowcase rows={data.items} />;
      case PAGE_SECTIONS.IMAGE_COPY:
        return <PageSectionImageCopy content={data} isFlipped={data.isFlipped} />;
      case PAGE_SECTIONS.SECONDARY_NAVIGATION:
        return (
          <PageSectionSecondaryNavigation
            items={data.items}
            heading={data.heading}
            renderPageSection={this.renderPageSection}
          />
        );
      case PAGE_SECTIONS.ASSET_DECORATED_CAROUSEL: {
        const assetDecoratedCarousel = new PageSectionAssetDecoratedCarouselModel(data);

        return (
          <PageSectionAssetDecoratedCarousel
            data={assetDecoratedCarousel}
            trackEvent={trackEvent}
          />
        );
      }
      case PAGE_SECTIONS.TESTIMONIAL_CAROUSEL: {
        const _data = new PageSectionTestimonialCarouselModel(data);

        return <PageSectionTestimonialCarousel data={_data} trackEvent={trackEvent} />;
      }
      case PAGE_SECTIONS.FEATURED_SLIDESHOW: {
        const _data = new PageSectionFeaturedSlideShowModel(data);

        return <PageSectionFeaturedSlideShow data={_data} trackEvent={trackEvent} />;
      }
      case PAGE_SECTIONS.CARD_CAROUSEL: {
        const _data = new CardCarouselModel(data);

        return (
          <CardCarousel data={_data} trackEvent={trackEvent} openVideoModal={this.openVideoModal} />
        );
      }
      case PAGE_SECTIONS.EMAIL_MODULE:
        return <EmailSignupModule onSubmit={this.onEmailSubmit} {...data} />;
      default:
        return null;
    }
  };

  render() {
    const {
      hero,
      heroType,
      scheduledDropDatetime,
      hasCountdownTimer,
      pageSections,
      isModalOpen,
      closeModal,
      modalSettings,
      trackEvent,
    } = this.props;

    const { modalVideoUrl } = this.state;

    return (
      <div
        className={classNames('LandingPage', {
          'LandingPage--perform-landing': this.isPerformLanding(),
        })}
      >
        {hero && (
          <>
            <VideoModal
              isOpen={isModalOpen}
              videoUrl={modalVideoUrl}
              closeModal={closeModal}
              modalSettings={modalSettings}
            />
            {[HERO_TYPES.PRIMARY_HERO, HERO_TYPES.PRIMARY_HERO_V3].includes(heroType) && (
              <HeroCarousel trackEvent={trackEvent}>
                {map(hero.heroSlides, (heroSlide, i) => (
                  <HeroSlide
                    key={i}
                    data={heroSlide}
                    trackEvent={trackEvent}
                    overlayPositionDesktop={hero.overlayPositionDesktop}
                    overlayPositionMobile={hero.overlayPositionMobile}
                    scheduledDropDatetime={scheduledDropDatetime}
                    hasCountdownTimer={hasCountdownTimer}
                    videoUrl={get(hero.heroSlides, 'length') === 1 ? hero.videoUrl : ''}
                    isModalOpen={isModalOpen}
                    openVideoModal={this.openVideoModal}
                  />
                ))}
              </HeroCarousel>
            )}
            {heroType === HERO_TYPES.SECONDARY_PROMO_HERO && <SecondaryPromoHero data={hero} />}
          </>
        )}

        <Grid>
          {map(pageSections, (data, i) => (
            <Cell smallUp={{ columns: 12 }} key={i}>
              <div className="LandingPage__page-section">{this.renderPageSection(data)}</div>
            </Cell>
          ))}
        </Grid>
        <style jsx>
          {`
            .LandingPage {
              background-color: ${Theme.get('colors-white')};
              padding-bottom: ${Theme.get('spacing') * 8}px;
            }

            @media ${mediumUp} {
              .LandingPage {
                padding-bottom: ${Theme.get('spacing') * 7}px;
              }
            }

            @media ${largeUp} {
              .LandingPage {
                padding-bottom: 0;
              }
            }

            .LandingPage__page-section {
              padding-bottom: ${Theme.get('spacing') * 12}px;
            }

            @media ${mediumUp} {
              .LandingPage__page-section {
                padding-bottom: ${Theme.get('spacing') * 13}px;
              }
            }

            @media ${largeUp} {
              .LandingPage__page-section {
                padding-bottom: ${Theme.get('spacing') * 20}px;
              }
            }
          `}
        </style>
        <style jsx global>
          {`
            .LandingPage .HomepageV2SecondaryPromoHero {
              padding-bottom: ${Theme.get('spacing') * 8}px;
            }

            .LandingPage .HomepageV2SecondaryPromoHero .typography--secondary-heading {
              font-size: 32px;
            }

            @media ${mediumUp} {
              .LandingPage .HomepageV2SecondaryPromoHero .typography--secondary-heading {
                font-size: 48px;
              }
            }
          `}
        </style>
      </div>
    );
  }
}

LandingPage.propTypes = {
  hero: PropTypes.oneOfType([
    PropTypes.instanceOf(HeroModel),
    PropTypes.instanceOf(SecondaryPromoHeroModel),
  ]),
  heroType: PropTypes.string,
  pageSections: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isModalOpen: PropTypes.bool,
  openModal: PropTypes.func,
  closeModal: PropTypes.func,
  trackEvent: PropTypes.func,
  signUpForNewsletter: PropTypes.func,
  modalSettings: PropTypes.shape({}),
  scheduledDropDatetime: PropTypes.instanceOf(Date),
  hasCountdownTimer: PropTypes.bool.isRequired,
};

LandingPage.defaultProps = {
  hero: null,
  heroType: HERO_TYPES.PRIMARY_HERO,
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  trackEvent: () => {},
  modalSettings: {},
  scheduledDropDatetime: new Date(),
  signUpForNewsletter: () => {},
};

export default LandingPage;
